import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SiteFooter } from "@/components/SiteFooter";
import { toast } from "sonner";
import { ArrowLeft, Gauge, Cpu, Info, Plus, ImagePlus, X } from "lucide-react";

const METER_TYPES = [
  { v: "electric", l: "Electric" }, { v: "gas", l: "Gas" },
  { v: "water", l: "Water" }, { v: "other", l: "Other" },
];

export default function MeterSerialVault() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showMeter, setShowMeter] = useState(false);
  const [showEquip, setShowEquip] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.get(`/properties/${propertyId}/meter-serial-vault`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const submitMeter = async (fd) => {
    setSaving(true);
    try {
      await api.post(`/properties/${propertyId}/meter-serial-vault/meter-readings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Meter reading saved"); setShowMeter(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save reading"); }
    finally { setSaving(false); }
  };
  const submitEquip = async (fd) => {
    setSaving(true);
    try {
      await api.post(`/properties/${propertyId}/meter-serial-vault/equipment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Equipment record saved"); setShowEquip(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save record"); }
    finally { setSaving(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-[#FAF7F2] grid place-items-center p-6" data-testid="vault-error">
      <div className="text-center"><p className="text-[#B91C1C]">Couldn't load the vault.</p>
        <button onClick={() => navigate(-1)} className="mt-3 text-sm font-medium text-brand hover:underline">Go back</button></div>
    </div>
  );

  const ro = data?.archived;

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col" data-testid="meter-serial-vault-page">
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="vault-logo-link"><Logo /></Link>
          <button onClick={() => navigate(`/property/${propertyId}`)} data-testid="vault-back-link"
            className="flex items-center gap-1.5 text-sm text-[#475569] hover:text-brand"><ArrowLeft size={15} /> Property</button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {!data ? (
          <div className="space-y-4"><Skeleton className="h-10 w-1/2 rounded-lg" /><Skeleton className="h-40 rounded-2xl" /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-brand text-sm font-medium"><Gauge size={16} /> Meter &amp; Serial Vault</div>
            <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-navy" data-testid="vault-title">Meter &amp; Serial Vault</h1>

            {ro && <div className="mt-4 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]" data-testid="vault-archived-note">This property is archived. Everything here is read-only.</div>}

            <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-xs text-[#475569]" data-testid="vault-disclaimer">
              <Info size={15} className="mt-0.5 shrink-0 text-[#94A3B8]" /><span>{data.disclaimer}</span>
            </div>

            {/* Meter readings */}
            <section className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="vault-meters-section">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2"><span className="grid place-items-center h-8 w-8 rounded-xl bg-brand/10 text-brand"><Gauge size={16} /></span>
                  <h2 className="font-heading text-lg font-semibold text-navy">Utility meter readings</h2></div>
                {!ro && <Button size="sm" variant="outline" data-testid="add-meter-btn" onClick={() => setShowMeter((v) => !v)} className="rounded-xl shrink-0"><Plus size={15} className="mr-1" /> Add</Button>}
              </div>
              {showMeter && !ro && <MeterForm rooms={data.rooms} saving={saving} onSubmit={submitMeter} onCancel={() => setShowMeter(false)} />}
              {data.meter_readings.length === 0 ? (
                <p className="mt-3 text-sm text-[#94A3B8]" data-testid="vault-meters-empty">No readings yet.</p>
              ) : (
                <ul className="mt-3 space-y-2" data-testid="vault-meters-list">
                  {data.meter_readings.map((m) => (
                    <li key={m.reading_id} data-testid={`meter-row-${m.reading_id}`} className="flex gap-3 rounded-xl border border-[#E2E8F0] px-3 py-2.5">
                      {m.image_path && <img src={`${API}${m.image_path}`} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-navy truncate">{m.label || METER_TYPES.find((t) => t.v === m.meter_type)?.l || m.meter_type}</p>
                        <p className="text-sm text-navy">{m.user_confirmed_reading}{m.unit ? ` ${m.unit}` : ""}</p>
                        <p className="text-[11px] text-[#94A3B8]">{m.recorded_date || "—"} · {m.date_source_label}</p>
                        {m.note && <p className="mt-0.5 text-xs text-[#64748B]">{m.note}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Equipment / serials */}
            <section className="mt-5 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="vault-equipment-section">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2"><span className="grid place-items-center h-8 w-8 rounded-xl bg-brand/10 text-brand"><Cpu size={16} /></span>
                  <h2 className="font-heading text-lg font-semibold text-navy">Appliances &amp; serial numbers</h2></div>
                {!ro && <Button size="sm" variant="outline" data-testid="add-equipment-btn" onClick={() => setShowEquip((v) => !v)} className="rounded-xl shrink-0"><Plus size={15} className="mr-1" /> Add</Button>}
              </div>
              {showEquip && !ro && <EquipForm rooms={data.rooms} saving={saving} onSubmit={submitEquip} onCancel={() => setShowEquip(false)} />}
              {data.equipment.length === 0 ? (
                <p className="mt-3 text-sm text-[#94A3B8]" data-testid="vault-equipment-empty">No equipment recorded yet.</p>
              ) : (
                <ul className="mt-3 space-y-2" data-testid="vault-equipment-list">
                  {data.equipment.map((e) => (
                    <li key={e.equipment_id} data-testid={`equipment-row-${e.equipment_id}`} className="flex gap-3 rounded-xl border border-[#E2E8F0] px-3 py-2.5">
                      {e.image_path && <img src={`${API}${e.image_path}`} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-navy truncate">{e.label}{e.room_name ? ` · ${e.room_name}` : ""}</p>
                        <p className="text-xs text-[#475569]">{[e.manufacturer, e.model].filter(Boolean).join(" ") || "—"}</p>
                        {e.serial && <p className="text-[11px] text-[#64748B] font-mono break-all">S/N: {e.serial}</p>}
                        {e.note && <p className="mt-0.5 text-xs text-[#64748B]">{e.note}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function PhotoPicker({ file, setFile, testId }) {
  return (
    <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer" data-testid={`${testId}-label`}>
      <ImagePlus size={15} className="text-[#94A3B8]" />
      <span>{file ? file.name : "Add a supporting photo (optional)"}</span>
      <input data-testid={testId} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
    </label>
  );
}

function MeterForm({ saving, onSubmit, onCancel }) {
  const [reading, setReading] = useState("");
  const [type, setType] = useState("electric");
  const [label, setLabel] = useState(""); const [unit, setUnit] = useState("");
  const [date, setDate] = useState(""); const [note, setNote] = useState(""); const [file, setFile] = useState(null);
  const submit = () => {
    const fd = new FormData();
    fd.append("reading", reading); fd.append("meter_type", type);
    if (label) fd.append("label", label); if (unit) fd.append("unit", unit);
    if (date) fd.append("recorded_date", date); if (note) fd.append("note", note);
    if (file) fd.append("file", file);
    onSubmit(fd);
  };
  return (
    <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-2" data-testid="meter-form">
      <div className="flex gap-2">
        <select data-testid="meter-type" value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-[#E2E8F0] px-2 py-2 text-sm bg-white">
          {METER_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <Input data-testid="meter-reading" placeholder="Reading (digits)" value={reading} onChange={(e) => setReading(e.target.value)} />
        <Input data-testid="meter-unit" placeholder="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} className="w-24" />
      </div>
      <Input data-testid="meter-label" placeholder="Label (e.g. Main electric)" value={label} onChange={(e) => setLabel(e.target.value)} />
      <Input data-testid="meter-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <Input data-testid="meter-note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <PhotoPicker file={file} setFile={setFile} testId="meter-photo" />
      <div className="flex gap-2 pt-1">
        <Button size="sm" data-testid="meter-save" disabled={saving || !reading.trim()} onClick={submit} className="rounded-lg">{saving ? "Saving…" : "Save reading"}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="meter-cancel"><X size={14} /></Button>
      </div>
    </div>
  );
}

function EquipForm({ rooms, saving, onSubmit, onCancel }) {
  const [label, setLabel] = useState(""); const [room, setRoom] = useState("");
  const [mfr, setMfr] = useState(""); const [model, setModel] = useState("");
  const [serial, setSerial] = useState(""); const [note, setNote] = useState(""); const [file, setFile] = useState(null);
  const submit = () => {
    const fd = new FormData();
    fd.append("label", label);
    if (room) fd.append("room_id", room); if (mfr) fd.append("manufacturer", mfr);
    if (model) fd.append("model", model); if (serial) fd.append("serial", serial);
    if (note) fd.append("note", note); if (file) fd.append("file", file);
    onSubmit(fd);
  };
  return (
    <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-2" data-testid="equipment-form">
      <Input data-testid="equip-label" placeholder="Item label (e.g. Refrigerator)" value={label} onChange={(e) => setLabel(e.target.value)} />
      {rooms?.length > 0 && (
        <select data-testid="equip-room" value={room} onChange={(e) => setRoom(e.target.value)} className="w-full rounded-lg border border-[#E2E8F0] px-2 py-2 text-sm bg-white">
          <option value="">No specific room</option>
          {rooms.map((r) => <option key={r.room_id} value={r.room_id}>{r.name}</option>)}
        </select>
      )}
      <div className="flex gap-2">
        <Input data-testid="equip-mfr" placeholder="Manufacturer" value={mfr} onChange={(e) => setMfr(e.target.value)} />
        <Input data-testid="equip-model" placeholder="Model" value={model} onChange={(e) => setModel(e.target.value)} />
      </div>
      <Input data-testid="equip-serial" placeholder="Serial number" value={serial} onChange={(e) => setSerial(e.target.value)} />
      <Input data-testid="equip-note" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <PhotoPicker file={file} setFile={setFile} testId="equip-photo" />
      <div className="flex gap-2 pt-1">
        <Button size="sm" data-testid="equip-save" disabled={saving || !label.trim()} onClick={submit} className="rounded-lg">{saving ? "Saving…" : "Save record"}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="equip-cancel"><X size={14} /></Button>
      </div>
    </div>
  );
}
